import React from "react";
import type { TimeSlot } from "../store/types";
import { InputLoader } from "@/components/ui/InputLoader";
import { useTranslation } from "react-i18next";

interface AvailableTimesProps {
  selectedDate: string;
  timeSlots: TimeSlot[];
  selectedTimeSlotId: number | null;
  onSelectTimeSlot: (timeSlotId: number) => void;
  onBook: () => void;
  onClose: () => void;
  isBooking?: boolean;
}

export const AvailableTimes: React.FC<AvailableTimesProps> = ({
  selectedDate,
  timeSlots,
  selectedTimeSlotId,
  onSelectTimeSlot,
  onBook,
  onClose,
  isBooking = false,
}) => {
  const { t, i18n } = useTranslation();
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const locale = (i18n.language || "sv").startsWith("en") ? "en-US" : "sv-SE";
    return date.toLocaleDateString(locale, {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    const locale = (i18n.language || "sv").startsWith("en") ? "en-US" : "sv-SE";
    return date.toLocaleTimeString(locale, {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getDuration = (start: string, end: string) => {
    const startDate = new Date(start);
    const endDate = new Date(end);
    const diff = endDate.getTime() - startDate.getTime();
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

    if (hours > 0 && minutes > 0) {
      return `${hours} h ${minutes} min`;
    } else if (hours > 0) {
      return `${hours} h`;
    } else {
      return `${minutes} min`;
    }
  };

  return (
    <div className="available-times-container">
      {/* Header */}
      <div className="times-header">
        <div>
          <h3 className="times-title">{t("availableTimes.title")}</h3>
          <p className="times-date">{formatDate(selectedDate)}</p>
        </div>
        <button className="close-button" onClick={onClose} aria-label={t("availableTimes.closeAria")}>
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>

      {/* Time Slots List */}
      <div className="times-list">
        {timeSlots.length === 0 ? (
          <div className="empty-slots">
            <svg className="empty-icon" fill="none" viewBox="0 0 24 24">
              <path
                stroke="currentColor"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <p>{t("availableTimes.noSlots")}</p>
          </div>
        ) : (
          timeSlots.map((slot) => (
            <div
              key={slot.id}
              className={`time-slot-card ${
                selectedTimeSlotId === slot.id ? "selected" : ""
              }`}
              onClick={() => onSelectTimeSlot(slot.id)}
            >
              <div className="slot-content">
                <div className="slot-time">
                  <svg className="clock-icon" fill="none" viewBox="0 0 24 24">
                    <path
                      stroke="currentColor"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <div>
                    <div className="time-range">
                      {formatTime(slot.startDate)} - {formatTime(slot.endDate)}
                    </div>
                    <div className="duration">
                      {getDuration(slot.startDate, slot.endDate)}
                    </div>
                  </div>
                </div>
                {selectedTimeSlotId === slot.id && (
                  <svg
                    className="check-icon"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={3}
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Footer with Book Button */}
      {timeSlots.length > 0 && (
        <div className="times-footer">
          <button
            className="book-button"
            onClick={onBook}
            disabled={selectedTimeSlotId === null || isBooking}
          >
            {isBooking ? (
              <InputLoader />
            ) : (
              <>
                <svg
                  className="button-icon"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
                {t("availableTimes.bookSelected")}
              </>
            )}
          </button>
        </div>
      )}

      <style>{`
        .available-times-container {
          background: white;
          border-radius: 16px;
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
          display: flex;
          flex-direction: column;
          height: 100%;
          max-height: 700px;
          overflow: hidden;
          animation: slideInRight 0.3s ease-out;
        }

        .times-header {
          padding: 1.5rem;
          border-bottom: 2px solid #f0f0f0;
          display: flex;
          justify-content: space-between;
          align-items: start;
        }

        .times-title {
          font-size: 1.25rem;
          font-weight: 700;
          color: #1a1a1a;
          margin: 0 0 0.25rem 0;
        }

        .times-date {
          font-size: 0.875rem;
          color: #666;
          margin: 0;
        }

        .close-button {
          background: #f5f5f5;
          border: none;
          border-radius: 8px;
          width: 36px;
          height: 36px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.2s ease;
          color: #666;
        }

        .close-button:hover {
          background: #e5e5e5;
          color: #333;
        }

        .times-list {
          flex: 1;
          overflow-y: auto;
          padding: 1rem;
        }

        .empty-slots {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 3rem 1.5rem;
          text-align: center;
          color: #666;
        }

        .empty-icon {
          width: 64px;
          height: 64px;
          color: #d1d5db;
          margin-bottom: 1rem;
        }

        .time-slot-card {
          background: #f9fafb;
          border: 2px solid #e5e7eb;
          border-radius: 12px;
          padding: 1rem;
          margin-bottom: 0.75rem;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .time-slot-card:hover {
          background: #f3f4f6;
          border-color: #d1d5db;
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
        }

        .time-slot-card.selected {
          background: #eff6ff;
          border-color: #3b82f6;
          box-shadow: 0 4px 12px rgba(59, 130, 246, 0.2);
        }

        .slot-content {
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .slot-time {
          display: flex;
          align-items: center;
          gap: 0.75rem;
        }

        .clock-icon {
          width: 24px;
          height: 24px;
          color: #3b82f6;
          flex-shrink: 0;
        }

        .time-range {
          font-size: 1rem;
          font-weight: 600;
          color: #1a1a1a;
          margin-bottom: 0.25rem;
        }

        .duration {
          font-size: 0.875rem;
          color: #666;
        }

        .check-icon {
          width: 24px;
          height: 24px;
          color: #3b82f6;
          flex-shrink: 0;
        }

        .times-footer {
          padding: 1.5rem;
          border-top: 2px solid #f0f0f0;
        }

        .book-button {
          width: 100%;
          background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
          color: white;
          border: none;
          border-radius: 12px;
          padding: 1rem;
          font-size: 1rem;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
        }

        .book-button:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 8px 20px rgba(59, 130, 246, 0.4);
        }

        .book-button:disabled {
          opacity: 0.5;
          cursor: not-allowed;
          transform: none;
        }

        .button-icon {
          width: 20px;
          height: 20px;
        }

        @keyframes slideInRight {
          from {
            opacity: 0;
            transform: translateX(30px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        @media (max-width: 768px) {
          .available-times-container {
            max-height: none;
            border-radius: 16px 16px 0 0;
          }

          .times-header {
            padding: 1.25rem;
          }

          .times-title {
            font-size: 1.125rem;
          }

          .times-list {
            padding: 0.75rem;
          }

          .time-slot-card {
            padding: 0.875rem;
          }
        }
      `}</style>
    </div>
  );
};
